/** @format */

import {
  faCheck,
  faExternalLinkAlt,
  faFileUpload,
  faPlus,
  faSearch,
  faTimes,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { api } from '../../../../api.ts';
import NewTooltip from '../../../misc/NewTooltip';

// Memoized File Item Component
const FileItem = memo(
  ({
    file,
    isSelected,
    isExisting,
    onSelect,
    onView,
    getDisplayFileName,
    canView,
  }) => (
    <ListItem
      sx={{
        backgroundColor: isSelected ? 'primary.50' : 'background.paper',
        borderRadius: 1,
        mb: 0.5,
      }}
    >
      <ListItemText
        primary={
          <Box display="flex" alignItems="center" gap={1}>
            {file.name}
            {isExisting && (
              <Chip
                label="Déjà attaché"
                size="small"
                color="info"
                variant="outlined"
              />
            )}
          </Box>
        }
        secondary={`Fichier : ${getDisplayFileName(file.file)}`}
      />
      <ListItemSecondaryAction>
        <Box display="flex" gap={1}>
          {canView && onView && (
            <NewTooltip content="Voir le fichier">
              <IconButton size="small" onClick={onView}>
                <FontAwesomeIcon icon={faExternalLinkAlt} />
              </IconButton>
            </NewTooltip>
          )}
          <Button
            size="small"
            variant={isSelected ? 'contained' : 'outlined'}
            onClick={onSelect}
            startIcon={
              isSelected ? (
                <FontAwesomeIcon icon={faCheck} />
              ) : (
                <FontAwesomeIcon icon={faPlus} />
              )
            }
            disabled={isExisting}
          >
            {isSelected ? 'Sélectionné' : 'Sélectionner'}
          </Button>
        </Box>
      </ListItemSecondaryAction>
    </ListItem>
  ),
  (prevProps, nextProps) =>
    prevProps.file.id === nextProps.file.id &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isExisting === nextProps.isExisting,
);

FileItem.displayName = 'FileItem';

FileItem.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    file: PropTypes.string,
    url: PropTypes.string,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  isExisting: PropTypes.bool.isRequired,
  onSelect: PropTypes.func.isRequired,
  onView: PropTypes.func,
  getDisplayFileName: PropTypes.func.isRequired,
  canView: PropTypes.bool,
};

FileItem.defaultProps = {
  onView: null,
  canView: false,
};

function FileBrowserDialog({
  open,
  onClose,
  onSelect,
  existingFiles = [],
  // Configuration props
  endpoint,
  title,
  subtitle,
  searchPlaceholder,
  emptyMessage,
  nameLabel,
  namePlaceholder,
  uploadButtonLabel,
  uploadingLabel,
  errorLoadingMessage,
  errorUploadMessage,
  attachButtonLabel,
  canViewFiles,
  brandId,
}) {
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [newFile, setNewFile] = useState(null);
  const [error, setError] = useState('');
  const searchTimeoutRef = useRef(null);

  // Get existing file IDs to avoid duplicates - memoized to prevent re-renders
  const existingFileIds = useMemo(
    () => existingFiles.map((file) => file.id),
    [existingFiles],
  );

  const fetchFiles = async (searchQuery = '') => {
    setLoading(true);
    try {
      const response = await api.get(endpoint, {
        brand_id: brandId,
        search: searchQuery,
        page_size: 100,
      });
      setFiles(response.data.results || response.data || []);
    } catch (err) {
      setError(errorLoadingMessage);
      console.error('Failed to fetch files:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchFiles(searchTerm);
  };

  const handleFileSelect = (file) => {
    setSelectedFiles((prev) => {
      const isSelected = prev.some((f) => f.id === file.id);
      if (isSelected) {
        return prev.filter((f) => f.id !== file.id);
      }
      return [...prev, file];
    });
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setNewFile(file);
      // Auto-fill name from filename if empty
      if (!newFileName) {
        setNewFileName(file.name.replace(/\.[^/.]+$/, '')); // Remove extension
      }
    }
  };

  const handleUpload = async () => {
    if (!newFileName || !newFile) {
      setError('Veuillez remplir le nom et sélectionner un fichier');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', newFileName);
      formData.append('file', newFile);

      const response = await api.post(endpoint, {}, formData, {
        'Content-Type': 'multipart/form-data',
      });

      // Add the new file to the list and select it
      const newFileData = response.data;
      setFiles((prev) => [newFileData, ...prev]);
      setSelectedFiles((prev) => [...prev, newFileData]);

      // Reset form
      setNewFileName('');
      setNewFile(null);
      setActiveTab(0); // Switch back to browse tab
    } catch (err) {
      setError(errorUploadMessage);
      console.error('Failed to upload file:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = () => {
    onSelect(selectedFiles);
    onClose();
  };

  const openFile = (file) => {
    if (file.url) {
      window.open(file.url, '_blank');
    }
  };

  const isFileSelected = useCallback(
    (file) => selectedFiles.some((f) => f.id === file.id),
    [selectedFiles],
  );

  const isFileExisting = useCallback(
    (file) => existingFileIds.includes(file.id),
    [existingFileIds],
  );

  const getDisplayFileName = useCallback((fileUrl) => {
    if (!fileUrl) return '';

    // Split by "/" and get the last element (filename)
    const parts = fileUrl.split('/');
    const filename = parts[parts.length - 1];

    // Split by "?" and get the first element (remove query parameters)
    const cleanFilename = filename.split('?')[0];

    return cleanFilename;
  }, []);

  useEffect(() => {
    if (open) {
      setError('');
      setSelectedFiles([]);
      fetchFiles();
    }
  }, [open]);

  // Debounced search effect
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (open && searchTerm !== undefined && searchTerm.length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchFiles(searchTerm);
      }, 500);
    } else if (open && searchTerm !== undefined && searchTerm.length === 0) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchFiles('');
      }, 300);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, open]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6">{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 2 }}
        >
          <Tab label="Parcourir" iconPosition="start" />
          <Tab label="Ajouter" iconPosition="start" />
        </Tabs>

        {activeTab === 0 && (
          <Box>
            {/* Search */}
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs>
                <TextField
                  fullWidth
                  placeholder={searchPlaceholder}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <FontAwesomeIcon
                        icon={faSearch}
                        style={{ marginRight: 8 }}
                      />
                    ),
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={handleSearch}
                  disabled={loading}
                  sx={{ height: 56 }}
                >
                  Rechercher
                </Button>
              </Grid>
            </Grid>

            {/* Results */}
            <Box
              sx={{
                maxHeight: 400,
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                position: 'relative',
              }}
            >
              {loading && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  right={0}
                  bottom={0}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  backgroundColor="rgba(255, 255, 255, 0.9)"
                  zIndex={1}
                  borderRadius={1}
                >
                  <CircularProgress />
                </Box>
              )}

              {error ? (
                <Alert severity="error" sx={{ m: 2 }}>
                  {error}
                </Alert>
              ) : (
                <List sx={{ p: 0 }}>
                  {files.map((file, index) => (
                    <React.Fragment key={`file-${file.id}`}>
                      <FileItem
                        file={file}
                        isSelected={isFileSelected(file)}
                        isExisting={isFileExisting(file)}
                        onSelect={() => handleFileSelect(file)}
                        onView={canViewFiles ? () => openFile(file) : null}
                        getDisplayFileName={getDisplayFileName}
                        canView={canViewFiles}
                      />
                      {index < files.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                  {files.length === 0 && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      textAlign="center"
                      py={3}
                    >
                      {emptyMessage}
                    </Typography>
                  )}
                </List>
              )}
            </Box>
          </Box>
        )}

        {activeTab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              {uploadButtonLabel}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={nameLabel}
                  value={newFileName}
                  onChange={(e) => setNewFileName(e.target.value)}
                  placeholder={namePlaceholder}
                />
              </Grid>
              <Grid item xs={12}>
                <input
                  style={{ display: 'none' }}
                  id="file-browser-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={handleFileInputChange}
                />
                <label htmlFor="file-browser-file-input">
                  <Button
                    variant="outlined"
                    component="span"
                    fullWidth
                    startIcon={<FontAwesomeIcon icon={faFileUpload} />}
                    sx={{ height: 56 }}
                  >
                    {newFile
                      ? `Fichier sélectionné : ${newFile.name}`
                      : 'Choisir un fichier'}
                  </Button>
                </label>
              </Grid>
            </Grid>

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="contained"
                onClick={handleUpload}
                disabled={!newFileName || !newFile || uploading}
                startIcon={
                  uploading ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FontAwesomeIcon icon={faPlus} />
                  )
                }
              >
                {uploading ? uploadingLabel : attachButtonLabel}
              </Button>
            </Box>
          </Box>
        )}

        {/* Selected Files Summary */}
        {selectedFiles.length > 0 && (
          <Box mt={3}>
            <Divider sx={{ mb: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Sélectionnés ({selectedFiles.length})
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {selectedFiles.map((file) => (
                <Chip
                  key={file.id}
                  label={file.name}
                  onDelete={() => handleFileSelect(file)}
                  deleteIcon={<FontAwesomeIcon icon={faTimes} />}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={selectedFiles.length === 0}
        >
          Attacher {selectedFiles.length} fichier
          {selectedFiles.length > 1 ? 's' : ''}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

FileBrowserDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  existingFiles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      file: PropTypes.string,
    }),
  ),
  // Configuration props
  endpoint: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  searchPlaceholder: PropTypes.string.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  nameLabel: PropTypes.string.isRequired,
  namePlaceholder: PropTypes.string.isRequired,
  uploadButtonLabel: PropTypes.string.isRequired,
  uploadingLabel: PropTypes.string.isRequired,
  errorLoadingMessage: PropTypes.string.isRequired,
  errorUploadMessage: PropTypes.string.isRequired,
  attachButtonLabel: PropTypes.string.isRequired,
  canViewFiles: PropTypes.bool,
  brandId: PropTypes.number,
};

FileBrowserDialog.defaultProps = {
  existingFiles: [],
  canViewFiles: false,
  brandId: null,
};

export default FileBrowserDialog;
